import * as React from 'react';
import { formatPartialDate } from 'Utility/date';
import { fm } from 'Components/FormatMessage';
import EventStepper from '../../../../../../components/EventStepper';
import { StepperHeaderValuePair } from '../../../../../../components/EventStepper/components';
import { capitalize } from '../../../../../../utility/string';
import { mappedDeviceFields } from '../config';
import { useAppSelector } from 'Store/index';
import { RenderAlerts } from 'Components/Alerts';
import { Container, Item } from 'Components/Grid';
import { styled } from '@mui/system';

const StyledTableRow = styled('div')({ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1.5fr 0.15fr 2fr' });

const TableRow = ({ title, value }: { title: string; value?: string }): React.JSX.Element => (
  <StyledTableRow>
    <div>{fm(title)}</div>
    <div />
    <div style={{ fontWeight: 600 }}>{value ?? '-'}</div>
  </StyledTableRow>
);

const ResMedDevices = ({ devices }: IOwnProps): React.JSX.Element => {
  const resMedLink = useAppSelector((s: IState) => s.session.patientIntegrationDetails?.resMed);
  const prescriptionDevice = resMedLink?.prescriptionDevice;

  const latestDevice = devices?.[0];
  const mismatchedDevice =
    prescriptionDevice && latestDevice
      ? prescriptionDevice.serialNo === latestDevice.resMedId &&
        prescriptionDevice.deviceType === latestDevice.type &&
        prescriptionDevice.deviceTypeDesc === latestDevice.deviceModel
        ? undefined
        : prescriptionDevice
      : undefined;

  return (
    <React.Fragment>
      {mismatchedDevice && (
        <Container style={{ marginBottom: '2.5rem' }}>
          <Item xs={5} />
          <Item xs={true}>
            <RenderAlerts
              alerts={[
                {
                  id: 'resMedDeviceMismatchAlert',
                  textID: '',
                  customText: (
                    <React.Fragment>
                      <div style={{ fontWeight: 'bold' }}>{fm(`treatment.papTherapy.deviceMismatchMessage`)}</div>
                      <TableRow title="treatment.papTherapy.resMedId" value={mismatchedDevice.serialNo} />
                      <TableRow title="treatment.papTherapy.deviceModel" value={mismatchedDevice.deviceTypeDesc} />
                    </React.Fragment>
                  ),
                  severity: 'info',
                },
              ]}
              elevation={0}
              width={80}
            />
          </Item>
        </Container>
      )}
      <EventStepper
        name="devices"
        formData={{ onChange: () => '', document: {} }}
        stepLabelText={(d: IPapDevice): string => formatPartialDate(d.date)}
        stepContent={(d: IPapDevice): React.JSX.Element => (
          <React.Fragment>
            {['resMedId', 'deviceModel', 'type', 'mode']
              .filter(field => Object.values(mappedDeviceFields).includes(field as keyof IPapDevice))
              .map((field, index) => {
                const value = d[field as keyof IPapDevice];
                const k = !['resMedId', 'deviceModel', 'mode'].includes(field) ? `device${capitalize(field)}` : field;
                return (
                  <StepperHeaderValuePair
                    key={`${field}${index}`}
                    header={fm(`treatment.papTherapy.${k === 'deviceType' ? 'deviceNumber' : k}`)}
                    value={value}
                  />
                );
              })}
          </React.Fragment>
        )}
        addNewTextHeader="treatment.papTherapy.newDevice"
        addNewTextButton="treatment.papTherapy.newDevice"
        previousEventsTextHeader="treatment.papTherapy.devices"
        noPreviousEventsTextHeader="treatment.papTherapy.noDevices"
        editingElements={() => <></>}
        viewing={true}
        deleteMessage="treatment.papTherapy.deleteDevice"
        readOnlyEvents={devices}
      />
    </React.Fragment>
  );
};

interface IOwnProps {
  devices?: Array<IPapDevice>;
}

export default ResMedDevices;
